import React from 'react';
import { ThemeableElement, useTheme } from '@design-systems/theme';
import clsx from 'clsx';
import { MenuItemProps } from './MenuItem';
import styles from './Menu.css';
import {
  scrollNodeToView,
  findAllMatched,
  findArraysDifference
} from './MenuListHelpers';

export interface MenuListProps
  extends Exclude<ThemeableElement<'div'>, 'children'> {
  /** aria-label */
  'aria-label'?: string;
  /**
   * Determine whether focus on a menu-item when Menu opens
   *
   * @default false
   */
  autoFocus?: boolean;
  /** children MenuItem to render. */
  children: React.ReactElement<MenuItemProps>[];
  /**
   * Determine whether MenuItem can be searched by typing
   *
   * @default false
   */
  disableSearch?: boolean;
  /**
   * Determine which search type to apply while typing
   * full - search sequentially adding typed symbol from the beginning
   * first - mimic all behavior to match first char only
   * partial - search for the first occurrence in the middle of displayed items
   *
   * @default 'first'
   */
  searchMode?: 'full' | 'first' | 'partial';
  /** ID to be applied to the menu. */
  id?: string;
  /** Determine the maximum width of Menu, unit is px. */
  maxHeight?: string | number;
  /** Determine the minimum width of Menu, unit is px. */
  minWidth?: string | number;
  /**
   * An ARIA role to be assigned to Menu.
   *
   * @default 'menu'
   */
  menuRole?: string;
  /** Function to call on key down event. */
  onKeyDown?: React.KeyboardEventHandler;
  /** reference: callback that returns the ref for menu list */
  reference?: (c: HTMLDivElement | null) => void;
  /**
   * Scroll current selected item into view from Menu if necessary
   *
   * @default false
   */
  scrollToSelected?: boolean;
  /** Provide focus to string match */
  accessStringMatch?: string;
}

/**
 * MenuList component used in Menu (not exported)
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const MenuList = ({
  accessStringMatch,
  autoFocus = false,
  children,
  className,
  colorScheme,
  disableSearch = false,
  maxHeight,
  menuRole = 'menu',
  minWidth,
  onKeyDown,
  reference,
  scrollToSelected = false,
  searchMode = 'first',
  style,
  theme,
  ...html
}: MenuListProps) => {
  const [focusedItemIndex, setFocusedItemIndex] = React.useState(0);
  const [selectedIndices, setSelectedIndices] = React.useState<number[]>([]);
  const [searchString, setSearchString] = React.useState('');
  const [activeDescendant, setActiveDescendant] = React.useState('');
  const menuListRef = React.useRef<HTMLDivElement | null>(null);

  /** used to set the highlighted element */
  const setMenuItemFocus = (itemToFocus = focusedItemIndex) => {
    const newFocusedMenuItem: HTMLDivElement | null =
      menuListRef.current &&
      (menuListRef.current.children[itemToFocus] as HTMLDivElement);

    if (newFocusedMenuItem && !children[itemToFocus].props.disabled) {
      setActiveDescendant(newFocusedMenuItem.id);
      newFocusedMenuItem.focus();
    }
  };

  React.useEffect(() => {
    const [newFocusedItemIndex] = findAllMatched(
      children,
      (child: any) => !child.props.disabled
    );
    setFocusedItemIndex(newFocusedItemIndex || 0);

    reference && reference(menuListRef.current);
    autoFocus && setMenuItemFocus(newFocusedItemIndex);
  }, []);

  /** sets focus on an item when it is selected */
  const setFocusOnSelectedItem = (newChildren = children) => {
    const newSelectedIndices = findAllMatched(
      newChildren,
      (child: any) => child.props.selected
    );

    const [lastSelected] = findArraysDifference(
      selectedIndices,
      newSelectedIndices
    );
    setSelectedIndices(newSelectedIndices);

    const newFocusedItemIndex = lastSelected || focusedItemIndex;
    setFocusedItemIndex(newFocusedItemIndex);
    if (newFocusedItemIndex) {
      setMenuItemFocus(newFocusedItemIndex);
    }
  };

  /** scrolls selected item into view */
  const scrollItemToView = (newChildren = children) => {
    const [highlightIndex] = findAllMatched(
      newChildren,
      (child: React.ReactElement<MenuItemProps>) => child.props.highlighted
    );

    if (highlightIndex > -1) {
      scrollNodeToView(
        menuListRef.current && menuListRef.current.children[highlightIndex],
        menuListRef.current,
        'nearest'
      );
    } else {
      const newSelectedIndices = findAllMatched(
        newChildren,
        (child: React.ReactElement<MenuItemProps>) => child.props.selected
      );
      const [lastSelected] = findArraysDifference(
        selectedIndices,
        newSelectedIndices
      );
      setSelectedIndices(newSelectedIndices);

      if (lastSelected) {
        scrollNodeToView(
          menuListRef.current && menuListRef.current.children[lastSelected],
          menuListRef.current,
          'center'
        );
      }
    }
  };

  React.useEffect(() => {
    if (autoFocus) {
      setFocusOnSelectedItem(children);
    }
  }, [autoFocus]);

  React.useEffect(() => {
    if (autoFocus && focusedItemIndex) {
      setMenuItemFocus();
    }

    if (scrollToSelected) {
      scrollItemToView(children);
    }
  }, [autoFocus, scrollToSelected, focusedItemIndex, children]);

  /** set focus on the item that first matches the search params */
  const setFocusOnFirstMatchItem = (key: string) => {
    if (!['full', 'partial'].includes(searchMode)) {
      setSearchString('');
    }

    const newSearchString = `${searchString.toLowerCase()}${key.toLowerCase()}`;
    setSearchString(newSearchString);

    let newSelectedIndices = [];
    if (['partial'].includes(searchMode)) {
      newSelectedIndices = findAllMatched(children, (item: any) => {
        const { disabled, label, children: text } = item.props;
        return (
          !disabled &&
          ((text &&
            typeof text === 'string' &&
            text.toLowerCase().includes(newSearchString)) ||
            (label && label.toLowerCase().includes(newSearchString)))
        );
      });
    } else {
      newSelectedIndices = findAllMatched(children, (item: any) => {
        const { disabled, label, children: text } = item.props;
        return (
          !disabled &&
          ((text &&
            typeof text === 'string' &&
            text.toLowerCase().startsWith(newSearchString)) ||
            (label && label.toLowerCase().startsWith(newSearchString)))
        );
      });
    }

    const [matchIndex] = newSelectedIndices;
    setSelectedIndices(newSelectedIndices);
    if (matchIndex >= 0) {
      setFocusedItemIndex(matchIndex);
      setMenuItemFocus(matchIndex);
    }
  };

  React.useEffect(() => {
    !disableSearch &&
      accessStringMatch &&
      setFocusOnFirstMatchItem(accessStringMatch);
  }, [accessStringMatch, disableSearch]);

  /** handles when arrowKeys are pressed */
  const handleArrowKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    const { key } = event;
    let count = 0;

    let newFocusedItemIndex = focusedItemIndex;

    do {
      if (key === 'ArrowDown') {
        newFocusedItemIndex += 1;
      }

      if (key === 'ArrowUp') {
        newFocusedItemIndex -= 1;
      }

      if (newFocusedItemIndex < 0) {
        newFocusedItemIndex = children.length - 1;
      }

      if (newFocusedItemIndex === children.length) {
        newFocusedItemIndex = 0;
      }

      count += 1;
      setFocusedItemIndex(newFocusedItemIndex);
    } while (
      newFocusedItemIndex &&
      children[newFocusedItemIndex].props.disabled &&
      count < children.length
    );

    setFocusedItemIndex(newFocusedItemIndex);
    setMenuItemFocus(newFocusedItemIndex);
  };

  /** handles when keys are pressed */
  const handleAccessibilityKeySupport = (event: React.KeyboardEvent) => {
    event.persist();

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        setSearchString('');
        handleArrowKeyDown(event);
        break;
      case 'Home':
        event.preventDefault();
        setFocusedItemIndex(0);
        setSearchString('');
        setMenuItemFocus(0);
        break;
      case 'End':
        event.preventDefault();
        setFocusedItemIndex(children.length - 1);
        setSearchString('');
        setMenuItemFocus(children.length - 1);
        break;
      default:
        !disableSearch && event.key && setFocusOnFirstMatchItem(event.key);
        break;
    }
  };

  /** handles keyDown events */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    handleAccessibilityKeySupport(event);
    onKeyDown && onKeyDown(event);
  };

  const { themeClass } = useTheme({ theme, colorScheme }, styles);

  let ariaActiveDescendant: string | undefined;
  if (activeDescendant) {
    ariaActiveDescendant = activeDescendant;
  }

  const inlineStyle: React.CSSProperties = {};

  if (minWidth) {
    inlineStyle.minWidth = minWidth;
  }

  if (maxHeight) {
    inlineStyle.maxHeight = maxHeight;
  }

  /** Don't show anything in the menu if it's empty */
  if (!children) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/aria-activedescendant-has-tabindex
    <div
      {...html}
      ref={menuListRef}
      aria-activedescendant={ariaActiveDescendant}
      className={clsx(styles.menuListWrapper, themeClass, className)}
      style={{ ...inlineStyle, ...style }}
      role={menuRole}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

export default MenuList;
